import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useDocumentMeta } from "@/hooks/use-document-meta";
import { CATEGORY_COLORS } from "@/lib/constants";
import { Hexagon, ArrowRight, ArrowDown, Workflow, Users, PlayCircle, BookOpen } from "lucide-react";

function NodeCard({ category, name, position, highlight = false }) {
  const color = CATEGORY_COLORS[category] || "#E63946";
  return (
    <div className={`rounded-xl bg-zinc-900 border ${highlight ? "border-primary shadow-[0_0_18px_rgba(230,57,70,0.35)]" : "border-zinc-700"} p-3.5 text-left w-full`}>
      <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color }}>{category}</span>
      <p className="font-heading font-semibold text-sm mt-1.5 text-white leading-tight">{name}</p>
      <p className="text-[11px] text-zinc-400 mt-0.5">{position}</p>
    </div>
  );
}

function Connector() {
  return (
    <div className="flex flex-col items-center py-1">
      <div className="h-6 border-l-2 border-dashed border-primary/60" />
      <ArrowDown className="h-4 w-4 text-primary -mt-1" />
    </div>
  );
}

export default function HowItWorks() {
  const { user } = useAuth();
  useDocumentMeta(
    "How It Works — Infinite Sequence | Learn Brazilian Jiu-Jitsu Faster",
    "Infinite Sequence answers the question every grappler asks mid-roll: what am I supposed to do next? Map BJJ sequences as visual flowcharts, join your gym's shared library, and always know your options from every position."
  );

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Hexagon className="h-6 w-6 text-primary fill-primary/20" strokeWidth={2.5} />
            <span className="font-heading font-extrabold text-lg tracking-tight">INFINITE SEQUENCE</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard"><Button data-testid="hiw-dashboard-btn" className="bg-primary hover:bg-primary/90 text-white rounded">Back to app</Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block"><Button data-testid="hiw-login-btn" variant="ghost" className="text-muted-foreground hover:text-white">Log in</Button></Link>
                <Link to="/register"><Button data-testid="hiw-signup-btn" className="bg-primary hover:bg-primary/90 text-white rounded">Get Started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="pt-16">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-6 pt-20 pb-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs font-semibold tracking-[0.2em] uppercase mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> How it works
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            "What am I supposed<br className="hidden sm:block" /> to do <span className="text-primary">next</span>?"
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            It's the question every grappler asks mid-roll. You land a position, your training partner reacts,
            and for a split second your mind goes blank. Infinite Sequence exists to answer that question —
            before you ever step on the mat.
          </p>
        </section>

        {/* The problem */}
        <section className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-3">The problem</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Class teaches you techniques. Sparring throws you situations.</h2>
          <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
            <p>
              In a typical Brazilian Jiu-Jitsu class you learn one or two techniques in isolation — a sweep here, a
              choke there. But live sparring doesn't happen in isolation. It's a fast-moving chain of positions,
              reactions and scrambles, and the person who wins the exchange is usually the one who already knows
              their options from wherever they land.
            </p>
            <p>
              Most people don't freeze because they know too few techniques. They freeze because their knowledge
              isn't <span className="text-white font-medium">connected</span>. They know the D'Arce choke, and they
              know top half guard — but under pressure they've never mapped one to the other. That gap between
              "knowing a move" and "knowing when it's available" is what keeps beginners stuck for years.
            </p>
          </div>
        </section>

        {/* The answer: sequences + example */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-3">The answer</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Sequences: flowcharts that show you what comes next.</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed max-w-3xl">
            A sequence is a visual map of a real sparring situation. Every card is a technique with its own video.
            Every arrow is an option. Here's a real example — you've got <span className="text-white font-medium">top
            half guard</span>, your opponent is defending, and you need to know your options:
          </p>

          {/* Example flowchart */}
          <div className="mt-10 rounded-2xl border border-border bg-[#0c0c0c] p-6 sm:p-10" data-testid="hiw-example-sequence">
            <div className="max-w-xs mx-auto">
              <NodeCard category="Guard Retention" name="Half Guard (Top)" position="Open Guard" highlight />
              <Connector />
            </div>
            <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Four attacks…</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <NodeCard category="Submission" name="D'Arce Choke from Top Half Guard" position="Open Guard" />
              <NodeCard category="Submission" name="Guillotine Choke from Top Half Guard" position="Open Guard" />
              <NodeCard category="Submission" name="Kneebar from Top Half Guard" position="Open Guard" />
              <NodeCard category="Submission" name="Toe Hold from Top Half Guard" position="Open Guard" />
            </div>
            <div className="max-w-xs mx-auto mt-4">
              <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">…or advance position</p>
              <Connector />
              <NodeCard category="Control" name="Side Control (Top)" position="Side Control" />
            </div>
          </div>

          <div className="mt-8 space-y-4 text-muted-foreground leading-relaxed max-w-3xl">
            <p>
              One glance tells you everything: from top half guard you can hunt the <span className="text-white font-medium">D'Arce</span>,
              the <span className="text-white font-medium">guillotine</span>, the <span className="text-white font-medium">kneebar</span> or
              the <span className="text-white font-medium">toe hold</span> — or pass to <span className="text-white font-medium">side control</span> and
              keep advancing. Tap any card and its video plays, so you can study the grips and details on the spot.
            </p>
            <p>
              Drill the map a few times and something changes in sparring: when you land in half guard, you're no
              longer searching for an answer. You already know the five doors in front of you — you're just picking
              which one your opponent has left open.
            </p>
          </div>
        </section>

        {/* Document + Gym */}
        <section className="max-w-5xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="p-7 rounded-xl bg-card border border-border">
              <Workflow className="h-7 w-7 text-primary mb-5" strokeWidth={2} />
              <h3 className="font-heading font-semibold text-lg mb-2">Document your own game</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Coach showed a killer sequence in class tonight? Capture it before you forget. Drag techniques onto
                the canvas, connect them with arrows, and attach a video to every step — your own uploads or YouTube
                links. Over months you build a living map of <em>your</em> jiu-jitsu, one that grows every time you learn
                something new.
              </p>
            </div>
            <div className="p-7 rounded-xl bg-card border border-border">
              <Users className="h-7 w-7 text-primary mb-5" strokeWidth={2} />
              <h3 className="font-heading font-semibold text-lg mb-2">Join a gym, inherit its playbook</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Join your gym with an invite code and its whole shared library of sequences is yours — the exact
                systems your coaches teach, mapped and ready to study. Everyone on the mat learns the same
                connected game, and new members skip months of "what was that move called again?"
              </p>
            </div>
            <div className="p-7 rounded-xl bg-card border border-border">
              <PlayCircle className="h-7 w-7 text-primary mb-5" strokeWidth={2} />
              <h3 className="font-heading font-semibold text-lg mb-2">Video on every node</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A name on a flowchart isn't enough — details win exchanges. Every technique card carries its own
                video, so reviewing a sequence the night before class means actually re-watching the grips, angles
                and finishing mechanics, not just skimming a list.
              </p>
            </div>
            <div className="p-7 rounded-xl bg-card border border-border">
              <BookOpen className="h-7 w-7 text-primary mb-5" strokeWidth={2} />
              <h3 className="font-heading font-semibold text-lg mb-2">Why this makes you faster</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Hesitation is the most expensive habit in jiu-jitsu. When your options from each position are mapped,
                drilled and reviewed, you stop losing the seconds where you wonder what's next — and you start
                chaining attacks the way higher belts do. Same mat time, dramatically more progress.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Stop guessing. Start flowing.</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Map your first sequence in minutes. Free for 7 days — then $10/month.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to={user ? "/sequences/new" : "/register"}>
              <Button data-testid="hiw-cta-btn" size="lg" className="bg-primary hover:bg-primary/90 text-white rounded h-12 px-7 text-base group">
                {user ? "Build a sequence" : "Start your free trial"} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><Hexagon className="h-4 w-4 text-primary" /> <span className="font-heading font-semibold text-white">INFINITE SEQUENCE</span></div>
          <span>Train smarter. Flow harder.</span>
        </div>
      </footer>
    </div>
  );
}
